import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useUpload } from "@/context/UploadContext";
import { Button } from "@/components/ui/button";
import { UserAccountMenu } from "@/components/shared/UserAccountMenu";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { AUTH_LOGO_INLINE } from "@/lib/authLogoInline";

const Header = ({ transparent = false }) => {
  const { user, isAdmin, logout } = useAuth();
  const { language, t, toggleLanguage } = useLanguage();
  const { isUploading } = useUpload();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showLandingNav = location.pathname === "/";

  const handleNavigation = (e, path) => {
    if (isUploading) {
      e.preventDefault();
      toast.error(
        language === "es"
          ? "No puedes navegar mientras se sube un video. Espera a que termine la subida."
          : "Cannot navigate while video is uploading. Wait for upload to complete."
      );
      return false;
    }
    setMobileMenuOpen(false);
    return true;
  };

  const handleLogout = () => {
    if (isUploading) {
      toast.error(
        language === "es"
          ? "No puedes cerrar sesión mientras se sube un video."
          : "Cannot logout while video is uploading."
      );
      return;
    }
    logout();
    setMobileMenuOpen(false);
    navigate("/");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${
        transparent ? "glass" : "bg-background border-b border-border"
      }`}
      data-testid="header"
    >
      <div className="container mx-auto flex items-center justify-between gap-2 px-4 py-3 sm:gap-3 sm:px-6 sm:py-3">
        <Link
          to="/"
          className={`header-logo-link flex min-w-0 items-center ${showLandingNav ? "flex-1" : "header-logo-link--compact-hitbox shrink-0 flex-none"}`}
          data-testid="logo-link"
        >
          <div className="header-logo-full-wrap">
            <img
              src={AUTH_LOGO_INLINE}
              alt="Voxara"
              className="header-logo-full"
              width="220"
              height="64"
              loading="eager"
              decoding="sync"
              data-testid="header-full-logo"
            />
            <div className="header-logo-full-glow"></div>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden xl:flex items-center gap-7">
          {showLandingNav && (
            <>
              <a
                href="#how"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors font-mono uppercase tracking-[0.18em]"
                data-testid="header-how-it-works-link"
              >
                {t("howItWorks")}
              </a>
              <a
                href="#features"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors font-mono uppercase tracking-[0.18em]"
                data-testid="header-features-link"
              >
                {t("features")}
              </a>
              <a
                href="#pricing"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors font-mono uppercase tracking-[0.18em]"
                data-testid="header-pricing-link"
              >
                {t("pricing")}
              </a>
            </>
          )}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden xl:flex items-center gap-4">
          {/* Language Toggle */}
          <div className="flex items-center rounded-xl overflow-hidden border border-border/70" data-testid="language-toggle">
            <button
              onClick={() => toggleLanguage("es")}
              className={`px-3.5 py-1.5 text-sm font-bold transition-all ${
                language === "es"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-muted-foreground hover:text-foreground"
              }`}
              data-testid="lang-es-btn"
            >
              ES
            </button>
            <button
              onClick={() => toggleLanguage("en")}
              className={`px-3.5 py-1.5 text-sm font-bold transition-all ${
                language === "en"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-muted-foreground hover:text-foreground"
              }`}
              data-testid="lang-en-btn"
            >
              EN
            </button>
          </div>

          {user ? (
            <>
              <UserAccountMenu user={user} isAdmin={isAdmin} onLogout={handleLogout} onBlockedNavigation={() => !isUploading} testIdPrefix="desktop-user-account-menu" />
            </>
          ) : (
            <>
              <Link to="/login">
                <Button
                  variant="ghost"
                  className="text-base font-semibold px-4 py-2.5"
                  data-testid="login-btn"
                >
                  {t("login")}
                </Button>
              </Link>
              <Link to="/signup">
                <Button
                  className="bg-primary text-primary-foreground hover:bg-primary/90 btn-glow text-base font-semibold px-5 py-2.5"
                  data-testid="signup-btn"
                >
                  {t("startFreeTrial")}
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2.5 xl:hidden">
          {user && (
            <UserAccountMenu
              user={user}
              isAdmin={isAdmin}
              onLogout={handleLogout}
              onBlockedNavigation={() => !isUploading}
              triggerClassName="max-w-[128px] gap-1.5 px-2 py-2 text-sm sm:max-w-[152px] sm:px-2.5 sm:text-base"
              usernameClassName="max-w-[4.1rem] sm:max-w-[5.25rem]"
              showAdminBadge={false}
              testIdPrefix="mobile-top-user-account-menu"
            />
          )}
          {/* Language Toggle - Mobile */}
          <div className="flex shrink-0 items-center overflow-hidden rounded-xl border border-border/70" data-testid="language-toggle-mobile">
            <button
              onClick={() => toggleLanguage("es")}
              className={`px-2 py-1.5 text-xs font-bold transition-all sm:px-2.5 sm:text-sm ${
                language === "es"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-muted-foreground"
              }`}
              data-testid="lang-es-btn-mobile"
            >
              ES
            </button>
            <button
              onClick={() => toggleLanguage("en")}
              className={`px-2 py-1.5 text-xs font-bold transition-all sm:px-2.5 sm:text-sm ${
                language === "en"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-muted-foreground"
              }`}
              data-testid="lang-en-btn-mobile"
            >
              EN
            </button>
          </div>
          
          {!user && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="mobile-header-menu-button shrink-0 p-2"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-background border-t border-border">
          <div className="container mx-auto px-4 py-4 space-y-3">
            {user ? (
              <>
                <div className="pb-3 border-b border-border">
                  <UserAccountMenu
                    user={user}
                    isAdmin={isAdmin}
                    onLogout={handleLogout}
                    onBlockedNavigation={() => !isUploading}
                    triggerClassName="w-full justify-start"
                    contentAlign="start"
                    testIdPrefix="mobile-panel-user-account-menu"
                  />
                </div>

                {showLandingNav && (
                  <div className="space-y-1 border-b border-border pb-3">
                    <a
                      href="#how"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 text-muted-foreground"
                      data-testid="mobile-header-how-it-works-link-auth"
                    >
                      {t("howItWorks")}
                    </a>
                    <a
                      href="#features"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 text-muted-foreground"
                      data-testid="mobile-header-features-link-auth"
                    >
                      {t("features")}
                    </a>
                    <a
                      href="#pricing"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 text-muted-foreground"
                      data-testid="mobile-header-pricing-link-auth"
                    >
                      {t("pricing")}
                    </a>
                  </div>
                )}
                
              </>
            ) : (
              <>
                <a
                  href="#how"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-muted-foreground"
                  data-testid="mobile-header-how-it-works-link"
                >
                  {t("howItWorks")}
                </a>
                <a
                  href="#features"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-muted-foreground"
                  data-testid="mobile-header-features-link"
                >
                  {t("features")}
                </a>
                <a
                  href="#pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-muted-foreground"
                  data-testid="mobile-header-pricing-link"
                >
                  {t("pricing")}
                </a>
                <div className="pt-3 border-t border-border space-y-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-header-login-link">
                    <Button variant="outline" className="w-full" data-testid="mobile-header-login-button">
                      {t("login")}
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-header-signup-link">
                    <Button className="w-full bg-primary text-primary-foreground" data-testid="mobile-header-signup-button">
                      {t("startFreeTrial")}
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
